import throttle from 'lodash/throttle';
import { registerFragment } from '@neptune/cx/registry';
import { AccordionSet } from '../cbdce-accordion-set';
import { DisposalSet, Json, onReady, select, canAnimateAuto } from '@neptune/cx/utils';
import './styles.scss';

const gc = new DisposalSet();

const ANIMATION_DURATION = 400;
const ANIMATION_INSTANT_DURATION = 32;

export const accordionRegistry = new Map<HTMLDetailsElement, Accordion>();

export class Accordion {
    isOpen = false;
    isAnimated = false;
    persistState = false;
    ident: string;
    parentSet: AccordionSet;
    parentAccordion: Accordion | null = null;
    // childAccordions: Set<Accordion> = new Set();
    accordionEl: HTMLDetailsElement;
    titleEl: HTMLElement;
    contentEl: HTMLElement;
    animationTimeout: NodeJS.Timeout | null = null;
    noInitialResize = false;

    constructor(accordionEl: HTMLDetailsElement, isEditMode: boolean) {
        if (accordionRegistry.has(accordionEl)) {
            return accordionRegistry.get(accordionEl) as Accordion;
        }

        this.accordionEl = accordionEl;
        this.titleEl = select(accordionEl, '.cbdce-accordion__title');
        this.contentEl = select(accordionEl, '.cbdce-accordion__content');
        if (accordionEl.classList.contains('cbdce-accordion--no-initial-resize')) {
            this.noInitialResize = true;
        }
        if (this.accordionEl.classList.contains('cbdce-accordion--open')) {
            this.isOpen = true;
        }

        this.ident = accordionEl.dataset.ident || '';
        this.persistState = accordionEl.dataset.persist === 'true' || false;

        const parentEl = accordionEl.closest<HTMLDivElement>('.cbdce-accordion-set');
        if (parentEl) {
            // start searching on parentElement because otherwise it would match itself
            let nearestParentAccordion = accordionEl.parentElement?.closest<HTMLDetailsElement>('.cbdce-accordion');
            if (!nearestParentAccordion || !parentEl.contains(nearestParentAccordion)) {
                this.parentSet = AccordionSet.registerAccordion(parentEl, this);
            } // else this looks like AccordionSet>Accordion>Accordion, so we'll just treat it as Accordion>Accordion
        }
        const parentAccordionEl = accordionEl.parentElement?.closest<HTMLDetailsElement>('.cbdce-accordion');
        if (parentAccordionEl) {
            this.parentAccordion = accordionRegistry.get(parentAccordionEl) ?? new Accordion(parentAccordionEl, isEditMode);
        }

        this.titleEl = select<HTMLElement>(accordionEl, '.cbdce-accordion__title');

        (gc.register(this.titleEl)
            .addListener('click', (e) => {
                const target = e.target as HTMLElement;
                if(isEditMode && (target.classList.contains('page-editor__editable') || target.classList.contains('cke_editable'))) {
                    e.preventDefault();
                    return;
                };

                e.preventDefault();
                e.stopPropagation();
                this.toggle();
                if (this.parentSet) this.parentSet.onOpen(this);
            })
        );

        accordionRegistry.set(accordionEl, this);

        if (!this.parentSet && !this.parentAccordion) {
            if (!this.noInitialResize) this.resize();
            this.setIsAnimated(true);
        } else if (this.parentAccordion) {
            if (!this.noInitialResize) this.resize();
            this.setIsAnimated(canAnimateAuto);
        }
    }
    setIsAnimated(isAnimated: boolean): void {
        if (this.isAnimated && !isAnimated) {
            this.isAnimated = false;
            this.accordionEl.classList.remove('cbdce-accordion--animated');
        } else if (!this.isAnimated && isAnimated) {
            this.isAnimated = true;
            this.accordionEl.classList.add('cbdce-accordion--animated');
        }
    }

    afterAnimation(isOpen: boolean): void {
        if (!isOpen) {
            this.accordionEl.removeAttribute('open');
        }
        this.contentEl.style.removeProperty('transition');
        this.animationTimeout = null;
    }

    close(instant: boolean = false): void {
        this.isOpen = false;
        this.accordionEl.classList.remove('cbdce-accordion--open')
        this.accordionEl.style.setProperty('--cbdce-accordion--content-height', '0px');
        if (instant || (this.parentAccordion && !canAnimateAuto)) {
            this.setIsAnimated(false);
        } else {
            this.setIsAnimated(true);
            const animation = this.contentEl.getAnimations({subtree: true});
            if (animation.length > 0) {
                animation[0].addEventListener('finish', () => this.afterAnimation(false));
            } else {
                if (this.animationTimeout) clearTimeout(this.animationTimeout);
                this.animationTimeout = setTimeout(() => this.afterAnimation(true), ANIMATION_INSTANT_DURATION);
            }
        }
        if (this.parentAccordion) {
            this.parentAccordion.resize();
        }
        if (this.persistState)  {
            updateState(this.ident, false);
        }
    }

    open(instant: boolean = false, skipResize: boolean = false): void {
        this.isOpen = true;

        this.accordionEl.classList.add('cbdce-accordion--open');
        this.accordionEl.setAttribute('open', '');
        if (!skipResize) {
            this.accordionEl.style.setProperty('--cbdce-accordion--content-height', `${this.contentEl.scrollHeight}px`);
        }
        if (instant || (this.parentAccordion && !canAnimateAuto)) {
            this.setIsAnimated(false);
        } else {
            this.setIsAnimated(true);
            const animation = this.contentEl.getAnimations({ subtree: true });

            if (animation.length > 0) {
                animation[0].addEventListener('finish', () => this.afterAnimation(true));
            } else {
                if (this.animationTimeout) clearTimeout(this.animationTimeout);
                this.animationTimeout = setTimeout(() => this.afterAnimation(true), ANIMATION_DURATION);
            }
        }
        if (this.parentAccordion) {
            this.parentAccordion.resize();
        }
        if (this.persistState)  {
            updateState(this.ident, true);
        }
    }

    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    resize(instant: boolean = false): void {
        if (this.isOpen) {
            if (instant) {
                this.setIsAnimated(false);
                this.accordionEl.style.setProperty('--cbdce-accordion--content-height', `${this.contentEl.scrollHeight}px`);
            } else {
                this.accordionEl.style.setProperty('--cbdce-accordion--content-height', `${this.contentEl.scrollHeight}px`);
            }
        }
    }
}

onReady(() => {
    (window as any)['cbdceAccordion'] = Accordion;
    (gc
        .register(window)
        .addListener('resize', throttle(e => {
            for (const accordion of accordionRegistry.values()) {
                accordion.resize();
            }
        }, 150)
    ));
});

const COOKIE_NAME = 'cbdce-accordion-state';
const MAX_STORED_STATES = 50;

type AccordionCookieState = {
    [key: string]: {
        open: boolean;
        lastSet: number;
    }
}

const pruneCookie = () => {
    const state = readCookie();
    let cookieCount = Object.keys(state).length;
    if (cookieCount > MAX_STORED_STATES) {
        const sortedStates = Object.entries(state)
            .sort(([, a], [, b]) => b.lastSet - a.lastSet)
            .slice(0, MAX_STORED_STATES);
        const prunedState: AccordionCookieState = Object.fromEntries(sortedStates);
        writeCookie(prunedState);
    }
}

const updateState = (ident: string, isOpen: boolean): void => {
    const state = readCookie();
    if (!state[ident]) {
        state[ident] = { open: false, lastSet: 0 };
    }
    state[ident] = { open: isOpen, lastSet: Date.now()};
    writeCookie(state);
}

const writeCookie = (state: AccordionCookieState): void => {
    const cookieValue = JSON.stringify(state);
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(cookieValue)}; path=/; max-age=${60*60*24*180};`;
}

const readCookie = (): AccordionCookieState => {
    const cookies: Record<string, Json> = (document.cookie
        .split(';')
        .reduce((res, c) => {
            const [key, val] = c.trim().split('=').map(decodeURIComponent)
            const allNumbers = str => /^\d+$/.test(str);
            try {
                return Object.assign(res, {[key]: allNumbers(val) ? val : JSON.parse(val)})
            } catch (e) {
                return Object.assign(res, {[key]: val})
            }
        }, {})
    );
    if (!cookies[COOKIE_NAME]) {
        return {};
    } else {
        return cookies[COOKIE_NAME] as AccordionCookieState;
    }
};

registerFragment('cbdce-accordion', (fragEl, configuration, meta) => {
    const isEditMode = document.body.classList.contains('has-edit-mode-menu');
    const accordionEl = select<HTMLDetailsElement>(fragEl, '.cbdce-accordion');
    const accordion = new Accordion(accordionEl, isEditMode);
});

onReady(() => {
    pruneCookie();
});

if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => {
        gc.disposeAll();
        accordionRegistry.clear();
    });
}
